<script setup lang="ts">
import BillingPlanSeal from '@/components/BillingPlanSeal/BillingPlanSeal.vue';
import { useI18n } from '@/i18n';
import { deriveAppUrl } from '@/lib/appUrl';
import { Button, Heading } from '@dabble/quillui';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

/** `upgradeName` is the tier just upgraded to, for a lifetime upgrade's own welcome. */
const props = defineProps<{ open: boolean; upgradeName?: string }>();
const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const appUrl = deriveAppUrl();

const title = computed(() =>
  props.upgradeName ? t('billing_welcome_upgrade_title', { name: props.upgradeName }) : t('billing_welcome_title'),
);
const copy = computed(() =>
  props.upgradeName ? t('billing_welcome_upgrade_copy', { name: props.upgradeName }) : t('billing_welcome_copy'),
);

const canvas = ref<HTMLCanvasElement | null>(null);
const closeButton = ref<InstanceType<typeof Button> | null>(null);

const CONFETTI_COLORS = ['#f5a503', '#f2385a', '#4ad9d9', '#36b1bf', '#ffd97a', '#ffffff'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  w: number;
  h: number;
  color: string;
  circle: boolean;
  wob: number;
}

let particles: Particle[] = [];
let raf: number | null = null;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function sizeCanvas() {
  const c = canvas.value;
  if (!c) return;
  const dpr = window.devicePixelRatio || 1;
  c.width = window.innerWidth * dpr;
  c.height = window.innerHeight * dpr;
  c.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function spawnBurst(x: number, y: number, count: number, angle: number, spread: number, power: number) {
  for (let i = 0; i < count; i++) {
    const a = angle + (Math.random() - 0.5) * spread;
    const p = power * (0.45 + Math.random() * 0.65);
    particles.push({
      x,
      y,
      vx: Math.cos(a) * p,
      vy: Math.sin(a) * p,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.28,
      w: 5 + Math.random() * 6,
      h: 8 + Math.random() * 7,
      color: CONFETTI_COLORS[(Math.random() * CONFETTI_COLORS.length) | 0],
      circle: Math.random() < 0.25,
      wob: Math.random() * Math.PI * 2,
    });
  }
}

function tick() {
  const c = canvas.value;
  const ctx = c?.getContext('2d');
  if (!c || !ctx) {
    raf = null;
    return;
  }
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
  particles = particles.filter((p) => p.y < window.innerHeight + 40);
  for (const p of particles) {
    p.vy += 0.13;
    p.vx *= 0.992;
    p.vy *= 0.992;
    p.wob += 0.08;
    p.x += p.vx + Math.sin(p.wob) * 0.6;
    p.y += p.vy;
    p.rot += p.vr;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.color;
    if (p.circle) {
      ctx.beginPath();
      ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.scale(1, Math.sin(p.wob * 0.7)); // paper flutter
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
    }
    ctx.restore();
  }
  raf = particles.length ? requestAnimationFrame(tick) : null;
}

function celebrate() {
  if (prefersReducedMotion()) return;
  sizeCanvas();
  const w = window.innerWidth;
  const h = window.innerHeight;
  spawnBurst(w * 0.08, h * 0.85, 70, -Math.PI / 2.6, 0.9, 16);
  spawnBurst(w * 0.92, h * 0.85, 70, -Math.PI + Math.PI / 2.6, 0.9, 16);
  setTimeout(() => {
    spawnBurst(w * 0.5, h * 0.3, 55, Math.PI / 2, Math.PI * 2, 5);
    if (!raf) raf = requestAnimationFrame(tick);
  }, 250);
  if (!raf) raf = requestAnimationFrame(tick);
}

function stopConfetti() {
  if (raf) cancelAnimationFrame(raf);
  raf = null;
  particles = [];
  const c = canvas.value;
  c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.open) emit('close');
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      celebrate();
      requestAnimationFrame(() => closeButton.value?.$el?.focus?.());
    }
  },
);

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', sizeCanvas);
  if (props.open) celebrate();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  window.removeEventListener('resize', sizeCanvas);
  stopConfetti();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="billing-lifetime-welcome"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      @click.self="emit('close')"
    >
      <div class="billing-lifetime-welcome__card">
        <div class="billing-lifetime-welcome__ribbon" />
        <div class="billing-lifetime-welcome__inner">
          <BillingPlanSeal class="billing-lifetime-welcome__seal" metal="gold" :top-text="t('billing_seal_lifetime')" />
          <p class="billing-lifetime-welcome__eyebrow">{{ t('billing_welcome_eyebrow') }}</p>
          <Heading
            :level="3"
            :semantic-level="2"
            family="secondary"
            weight="regular"
            :margin="false"
            class="billing-lifetime-welcome__title"
            >{{ title }}</Heading
          >
          <p class="billing-lifetime-welcome__copy">{{ copy }}</p>
          <Button ref="closeButton" variant="primary" finish="filled" size="large" :href="appUrl">
            {{ t('billing_welcome_start') }}
          </Button>
          <Button variant="secondary" finish="link" size="small" @click="emit('close')">
            {{ t('billing_welcome_back') }}
          </Button>
        </div>
      </div>
    </div>
    <canvas ref="canvas" class="billing-lifetime-welcome__confetti" aria-hidden="true" />
  </Teleport>
</template>

<style lang="scss" scoped>
.billing-lifetime-welcome {
  position: fixed;
  inset: 0;
  z-index: layer('modal');
  display: flex;
  align-items: center;
  justify-content: center;
  padding: space('medium');
  background: rgba(22, 17, 9, 0.45);
  -webkit-backdrop-filter: blur(10px) saturate(1.1);
  backdrop-filter: blur(10px) saturate(1.1);

  &__card {
    width: min(100%, 29rem);
    background: theme('color-fg-app');
    border-radius: borderRadius('medium');
    box-shadow: 0 4px 32px 0 rgba(0, 0, 0, 0.2);
    overflow: hidden;
    text-align: center;
    animation: billing-welcome-pop 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.2);

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &__ribbon {
    height: 6px;
    background: linear-gradient(
      to right,
      theme('color-brand-gold') 0 33.34%,
      theme('color-brand-red') 33.34% 66.67%,
      theme('color-brand-teal') 66.67% 100%
    );
  }

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: space('xxsmall');
    padding: space('xlarge') space('xlarge') space('xlarge');
  }

  &__seal {
    width: 10.125rem;
    margin-bottom: space('small');
    filter: drop-shadow(0 6px 14px rgba(150, 98, 0, 0.3));
  }

  &__eyebrow {
    margin: 0;
    font-size: fontSize('xsmall');
    font-weight: fontWeight('bold');
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #b47a02;
  }

  &__title {
    font-size: fontSize('xlarge');
    text-wrap: balance;
  }

  &__copy {
    margin: 0 0 space('medium');
    max-width: 36ch;
    font-size: fontSize('small');
    line-height: 1.5;
    color: theme('color-text-secondary');
  }

  &__confetti {
    position: fixed;
    inset: 0;
    z-index: layer('modal') + 1;
    pointer-events: none;
  }
}

@keyframes billing-welcome-pop {
  from {
    transform: translateY(18px) scale(0.96);
    opacity: 0;
  }
}
</style>
